import { Trans, t } from "@lingui/macro";
import { useEffect, useState } from "react";
import useSWR from "swr";
import cx from "classnames";

import { getPageTitle } from "lib/legacy";
import { fetchLeaderboard } from "lib/indexer/client";
import { CONFIG_UPDATE_INTERVAL } from "lib/timeConstants";
import { formatAmount } from "lib/numbers";
import { USD_DECIMALS } from "config/factors";
import { useChainId } from "lib/chains";

import SEO from "components/Common/SEO";
import Footer from "components/Footer/Footer";
import AddressView from "components/AddressView/AddressView";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipComponent from "components/Tooltip/Tooltip";

import "./LeaderboardV1.css";

const PAGE_SIZE = 50;

type Timeframe = "alltime" | "30d";
type SortMetric = "pnl" | "volume" | "trades";

function toBackendSort(metric: SortMetric, tf: Timeframe): string {
  return `${metric}_${tf === "30d" ? "30d" : "alltime"}`;
}

function getRankBadge(rank: number) {
  if (rank === 1) return { medal: "🥇", cls: "LB-rank-gold" };
  if (rank === 2) return { medal: "🥈", cls: "LB-rank-silver" };
  if (rank === 3) return { medal: "🥉", cls: "LB-rank-bronze" };
  return { medal: null, cls: "" };
}

export function LeaderboardV1() {
  const { chainId } = useChainId();
  const [timeframe, setTimeframe] = useState<Timeframe>("alltime");
  const [metric, setMetric] = useState<SortMetric>("pnl");
  const [page, setPage] = useState(0);

  useEffect(() => { setPage(0); }, [metric, timeframe]);

  const sort = toBackendSort(metric, timeframe);

  const { data, isLoading } = useSWR(
    ["leaderboardV1", sort, page],
    () => fetchLeaderboard({ sort, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    { refreshInterval: CONFIG_UPDATE_INTERVAL }
  );

  const rows = data?.items ?? [];
  const totalRows = data?.total ?? 0;
  const totalPages = Math.ceil(totalRows / PAGE_SIZE);

  return (
    <SEO title={getPageTitle(t`Leaderboard`)}>
      <div className="default-container page-layout">

        {/* ── Hero ────────────────────────────────────────── */}
        <div className="LB-hero">
          <div className="LB-hero-text">
            <h1 className="LB-hero-title">
              <Trans>Leaderboard</Trans>
            </h1>
            <p className="LB-hero-sub">
              <Trans>Top traders on MoneyX — BESC Hyperchain</Trans>
            </p>
          </div>
          {totalRows > 0 && (
            <div className="LB-hero-stat">
              <span className="LB-hero-stat-val">{totalRows.toLocaleString()}</span>
              <span className="LB-hero-stat-label">
                <Trans>Traders ranked</Trans>
              </span>
            </div>
          )}
        </div>

        {/* ── Filter bar ──────────────────────────────────── */}
        <div className="LB-filters">
          <div className="LB-pill-group">
            <span className="LB-pill-label"><Trans>Period</Trans></span>
            {(["alltime", "30d"] as Timeframe[]).map((tf) => (
              <button
                key={tf}
                className={cx("LB-pill", { "LB-pill--active": timeframe === tf })}
                onClick={() => setTimeframe(tf)}
              >
                {tf === "alltime" ? t`All Time` : t`30 Days`}
              </button>
            ))}
          </div>

          <div className="LB-pill-group">
            <span className="LB-pill-label"><Trans>Rank by</Trans></span>
            {(["pnl", "volume", "trades"] as SortMetric[]).map((m) => (
              <button
                key={m}
                className={cx("LB-pill", { "LB-pill--active": metric === m })}
                onClick={() => setMetric(m)}
              >
                {m === "pnl" ? t`PnL` : m === "volume" ? t`Volume` : t`Trades`}
              </button>
            ))}
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────── */}
        <div className="LB-table-wrap">
          <table className="LB-table">
            <thead>
              <tr className="LB-thead-row">
                <th className="LB-th LB-th-rank"><Trans>#</Trans></th>
                <th className="LB-th LB-th-trader"><Trans>Trader</Trans></th>
                <th className="LB-th LB-th-right"><Trans>PnL</Trans></th>
                <th className="LB-th LB-th-right LB-hide-sm"><Trans>Win Rate</Trans></th>
                <th className="LB-th LB-th-right LB-hide-sm"><Trans>Volume</Trans></th>
                <th className="LB-th LB-th-right"><Trans>Trades</Trans></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="LB-empty">
                    <div className="LB-loading">
                      <div className="LB-spinner" />
                      <span><Trans>Loading traders…</Trans></span>
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="LB-empty">
                    <div className="LB-empty-inner">
                      <div className="LB-empty-icon">🏆</div>
                      <div className="LB-empty-title"><Trans>No data yet</Trans></div>
                      <div className="LB-empty-body">
                        <Trans>Be the first to trade on MoneyX and claim the top spot!</Trans>
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {rows.map((row: any, i: number) => {
                const rank = page * PAGE_SIZE + i + 1;
                const { medal, cls } = getRankBadge(rank);
                const suffix = timeframe === "30d" ? "30d" : "alltime";

                const pnl = BigInt(row[`pnl_${suffix}`] ?? "0");
                const volume = BigInt(row[`volume_${suffix}`] ?? "0");
                const trades = row[`trades_${suffix}`] ?? 0;
                const wins = row[`wins_${suffix}`] ?? row["wins_alltime"] ?? 0;
                const losses = row[`losses_${suffix}`] ?? row["losses_alltime"] ?? 0;
                const total = wins + losses;
                const winRate = total > 0 ? (wins / total) * 100 : 0;
                const positive = pnl >= 0n;

                return (
                  <tr key={row.account} className={cx("LB-row", { "LB-row-top3": rank <= 3 })}>
                    <td className="LB-td LB-td-rank">
                      <span className={cx("LB-rank", cls)}>
                        {medal ? (
                          <span className="LB-medal">{medal}</span>
                        ) : (
                          <span className="LB-rank-num">#{rank}</span>
                        )}
                      </span>
                    </td>

                    <td className="LB-td LB-td-trader">
                      <AddressView address={row.account} size={20} />
                    </td>

                    <td className={cx("LB-td LB-td-right LB-td-pnl", positive ? "LB-pos" : "LB-neg")}>
                      <TooltipComponent
                        handle={
                          <span className="LB-pnl-handle">
                            {positive ? "+" : "−"}${formatAmount(pnl < 0n ? -pnl : pnl, USD_DECIMALS, 2, true)}
                          </span>
                        }
                        position="bottom-end"
                        content={
                          <>
                            <StatsTooltipRow
                              label={t`Realised PnL`}
                              value={`${positive ? "+" : "−"}$${formatAmount(pnl < 0n ? -pnl : pnl, USD_DECIMALS, 2, true)}`}
                              showDollar={false}
                            />
                            <StatsTooltipRow
                              label={t`Wins / Losses`}
                              value={`${wins} / ${losses}`}
                              showDollar={false}
                            />
                            {total > 0 && (
                              <StatsTooltipRow
                                label={t`Win Rate`}
                                value={`${winRate.toFixed(1)}%`}
                                showDollar={false}
                              />
                            )}
                          </>
                        }
                      />
                    </td>

                    <td className="LB-td LB-td-right LB-hide-sm">
                      {total > 0 ? (
                        <span className={cx("LB-winrate", winRate >= 60 ? "LB-pos" : winRate < 40 ? "LB-neg" : "")}>
                          {winRate.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="LB-muted">—</span>
                      )}
                    </td>

                    <td className="LB-td LB-td-right LB-hide-sm LB-muted">
                      ${formatAmount(volume, USD_DECIMALS, 0, true)}
                    </td>

                    <td className="LB-td LB-td-right LB-muted">
                      {trades}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ──────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="LB-pagination">
            <button
              className="LB-page-btn"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ← <Trans>Prev</Trans>
            </button>
            <div className="LB-page-info">
              <span className="LB-page-cur">{page + 1}</span>
              <span className="LB-page-sep">/</span>
              <span>{totalPages}</span>
            </div>
            <button
              className="LB-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <Trans>Next</Trans> →
            </button>
          </div>
        )}

        <Footer />
      </div>
    </SEO>
  );
}
